import { useEffect, useState } from 'react';
import { CheckCircle, Circle, Loader2, AlertCircle, XCircle } from 'lucide-react';
import type { JobStatus } from '../types';
import clsx from 'clsx';

type SystemType = 'factsagent' | 'cppages' | 'topicalmap' | 'images' | 'content' | 'serviceareapages' | 'briefcontent' | 'contentframework' | 'contentframeworkv2' | 'icpagent' | 'cpcustom';

interface ProgressMonitorProps {
  jobId: number;
  systemType?: SystemType;
  onStatusUpdate: (status: JobStatus) => void;
  pollInterval?: number;
  customSteps?: string[];
}

const SYSTEM_STEPS: Record<SystemType, string[]> = {
  factsagent: [
    'Clean Transcript',
    'Format Transcript',
    'Extract Facts & Policies',
    'Verify Completeness',
    'Check Duplicates',
    'Consolidate Final Facts',
  ],
  cppages: [
    'Loading Input Files',
    'Generating Pages',
    'Processing Content',
    'Saving Results',
  ],
  topicalmap: [
    'Analyzing Location',
    'Generating Map Structure',
    'Creating Pages',
    'Finalizing Output',
  ],
  images: [
    'Loading Configuration',
    'Generating Images',
    'Processing Output',
  ],
  content: [
    'Loading Content',
    'Generating Article',
    'Optimizing SEO',
    'Finalizing',
  ],
  serviceareapages: [
    'Loading Input Files',
    'Generating Location Pages',
    'Running QA Validation',
    'Saving Results',
  ],
  briefcontent: [
    'Loading Writing Brief',
    'Generating Content',
    'Running QA Validation',
    'Saving Results',
  ],
  contentframework: [
    'Setting Up Pipeline',
    'Running 7-Agent Pipeline',
    'Processing Output',
    'Saving Results',
  ],
  contentframeworkv2: [
    'Setting Up Pipeline',
    'Running 9-Agent Pipeline',
    'Processing Output',
    'Saving Results',
  ],
  icpagent: [
    'Reading Facts Data',
    'Generating ICP Document',
    'Saving Results',
  ],
  cpcustom: [
    'Loading Briefs',
    'Generating Pages',
    'Processing Output',
    'Saving Results',
  ],
};

const SYSTEM_ENDPOINTS: Record<SystemType, string> = {
  factsagent: '/api/factsagent/status',
  cppages: '/api/cppages/status',
  topicalmap: '/api/topicalmap/status',
  images: '/api/images/status',
  content: '/api/content/status',
  serviceareapages: '/api/serviceareapages/status',
  briefcontent: '/api/briefcontent/status',
  contentframework: '/api/contentframework/status',
  contentframeworkv2: '/api/contentframeworkv2/status',
  icpagent: '/api/icpagent/status',
  cpcustom: '/api/cpcustom/status',
};

// Average time per step in seconds (rough estimates)
const STEP_DURATION_ESTIMATES: Record<SystemType, number[]> = {
  factsagent: [120, 120, 300, 100, 80, 150],
  cppages: [30, 300, 120, 30],
  topicalmap: [60, 180, 120, 30],
  images: [30, 300, 60],
  content: [30, 180, 60, 30],
  serviceareapages: [30, 300, 60, 30],
  briefcontent: [30, 180, 60, 30],
  contentframework: [30, 600, 120, 30],
  contentframeworkv2: [30, 900, 120, 30],
  icpagent: [30, 180, 30],
  cpcustom: [15, 300, 60, 15],
};

export default function ProgressMonitor({
  jobId,
  systemType = 'factsagent',
  onStatusUpdate,
  pollInterval = 2000,
  customSteps,
}: ProgressMonitorProps) {
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string>('');
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const steps = customSteps || SYSTEM_STEPS[systemType] || SYSTEM_STEPS.factsagent;
  const endpoint = SYSTEM_ENDPOINTS[systemType] || SYSTEM_ENDPOINTS.factsagent;
  const stepDurations = STEP_DURATION_ESTIMATES[systemType] || STEP_DURATION_ESTIMATES.factsagent;

  // Calculate estimated remaining time
  const calculateRemainingTime = (completedSteps: number): number => {
    if (completedSteps >= steps.length) return 0;

    let remainingTime = 0;
    for (let i = completedSteps; i < steps.length; i++) {
      remainingTime += stepDurations[i] || 60;
    }
    return remainingTime;
  };

  // Format seconds to readable time
  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  // Timer to track elapsed time
  useEffect(() => {
    if (!status || status.status !== 'processing') return;

    if (!startTime) {
      setStartTime(Date.now());
    }

    const timerInterval = setInterval(() => {
      if (startTime) {
        setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [status, startTime]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch(`${endpoint}/${jobId}`, { headers });
        if (!response.ok) {
          throw new Error('Failed to fetch status');
        }
        const data: JobStatus = await response.json();
        setStatus(data);
        onStatusUpdate(data);

        // Initialize start time when processing begins
        if (data.status === 'processing' && !startTime) {
          setStartTime(Date.now());
        }

        // Stop polling if job is complete or failed
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch status');
      }
    };

    // Fetch immediately
    fetchStatus();

    // Then poll at interval
    interval = setInterval(fetchStatus, pollInterval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [jobId, pollInterval, onStatusUpdate, startTime, endpoint]);

  if (error) {
    return (
      <div className="card border-red-200 bg-red-50">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-3 text-gray-600">Loading status...</span>
        </div>
      </div>
    );
  }

  const currentStep = status.completed_items;
  const totalSteps = status.total_items || steps.length;
  const isProcessing = status.status === 'processing';
  const isCompleted = status.status === 'completed';
  const isFailed = status.status === 'failed';

  // For CPPages, show generators as steps based on progress message
  const displaySteps = status.total_items && status.total_items !== steps.length
    ? Array.from({ length: status.total_items }, (_, i) => `Generator ${i + 1}`)
    : steps;

  return (
    <div className="space-y-4">
      {/* Overall Progress */}
      <div className="card">
        <div className="mb-4">
          {/* Display job name with project and agent info */}
          {status.display_name && (
            <div className="mb-3 pb-3 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-500">Current Task</p>
              <p className="text-lg font-semibold text-gray-900">{status.display_name}</p>
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Overall Progress
            </h3>
            <span
              className={clsx(
                'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                isProcessing && 'bg-blue-100 text-blue-800',
                isCompleted && 'bg-green-100 text-green-800',
                isFailed && 'bg-red-100 text-red-800',
                !isProcessing && !isCompleted && !isFailed && 'bg-gray-100 text-gray-800'
              )}
            >
              {status.status}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-500',
                isFailed ? 'bg-red-500' : 'bg-primary-600'
              )}
              style={{ width: `${status.progress}%` }}
            />
          </div>

          <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
            <span>
              {status.completed_items} of {totalSteps} steps completed
            </span>
            <span>{status.progress}%</span>
          </div>

          {/* Current message */}
          {status.message && (
            <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
              {status.message}
            </div>
          )}

          {/* Time Information */}
          {isProcessing && (
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                <div>
                  <span className="text-gray-500">Elapsed: </span>
                  <span className="font-medium text-gray-900">{formatTime(elapsedSeconds)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Remaining: </span>
                  <span className="font-medium text-gray-900">
                    ~{formatTime(calculateRemainingTime(status.completed_items))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {status.error_message && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{status.error_message}</p>
          </div>
        )}
      </div>

      {/* Step-by-Step Progress */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Processing Steps
        </h3>

        <div className="space-y-3">
          {displaySteps.map((stepName, index) => {
            const stepNumber = index + 1;
            const isComplete = stepNumber <= currentStep;
            const isCurrent = stepNumber === currentStep + 1 && isProcessing;
            // When job failed: the step right after completed ones is the failed step
            const isFailedStep = isFailed && stepNumber === currentStep + 1;
            // Steps beyond the failed step that never ran
            const isSkippedByFailure = isFailed && stepNumber > currentStep + 1;

            return (
              <div
                key={stepNumber}
                className={clsx(
                  'flex items-start p-3 rounded-lg transition-colors',
                  isComplete && 'bg-green-50',
                  isCurrent && 'bg-blue-50',
                  isFailedStep && 'bg-red-50',
                  isSkippedByFailure && 'bg-gray-50',
                  !isComplete && !isCurrent && !isFailedStep && !isSkippedByFailure && 'bg-gray-50'
                )}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {isComplete ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : isCurrent ? (
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  ) : isFailedStep ? (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  ) : isSkippedByFailure ? (
                    <XCircle className="h-5 w-5 text-red-400" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className={clsx(
                      'text-sm font-medium',
                      isComplete && 'text-green-900',
                      isCurrent && 'text-blue-900',
                      isFailedStep && 'text-red-900',
                      isSkippedByFailure && 'text-red-400',
                      !isComplete && !isCurrent && !isFailedStep && !isSkippedByFailure && 'text-gray-600'
                    )}
                  >
                    Step {stepNumber}: {stepName}
                  </p>
                  {isCurrent && (
                    <p className="text-xs text-blue-700 mt-1">
                      Processing...
                    </p>
                  )}
                  {isFailedStep && (
                    <p className="text-xs text-red-600 mt-1">
                      Failed
                    </p>
                  )}
                  {isSkippedByFailure && (
                    <p className="text-xs text-red-400 mt-1">
                      Skipped
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Live Logs */}
      {status.recent_logs && status.recent_logs.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Recent Logs
          </h3>
          <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs text-gray-300 space-y-1 max-h-48 overflow-y-auto">
            {status.recent_logs.map((log, index) => (
              <div key={index} className="text-green-400">
                {log}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
